import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuthWrapperContext } from 'Contexts/AuthWrapperContext';

/**
 * Renders a repertoire preview image whose `src` is a short-lived presigned URL
 * (private user-repertoire assets, Decision #157). Presigned URLs expire after
 * ~1 hour, so if the browser fails to load the image (e.g. a Library tab left
 * open past expiry), this re-fetches the repertoire once to obtain a fresh
 * presigned preview URL and retries. Museflow curriculum previews are plain CDN
 * URLs and simply never hit the refresh path.
 */
type PresignedImgProps = {
  repertoireId: number;
  src?: string;
  alt?: string;
  style?: React.CSSProperties;
  className?: string;
};

const PresignedImg: React.FC<PresignedImgProps> = ({
  repertoireId,
  src,
  alt,
  style,
  className,
}) => {
  const { currentAuthToken } = useAuthWrapperContext();
  const [currentSrc, setCurrentSrc] = useState<string | undefined>(src);
  const refreshedRef = useRef(false);

  // Keep in sync if the parent supplies a new src (e.g. list refetch).
  useEffect(() => {
    setCurrentSrc(src);
    refreshedRef.current = false;
  }, [src]);

  const handleError = async () => {
    // Only attempt a single refresh, to avoid an error loop on a genuinely
    // missing/broken asset.
    if (refreshedRef.current) return;
    refreshedRef.current = true;
    try {
      const token = await currentAuthToken();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/repertoires/${repertoireId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const fresh: string | undefined = response?.data?.repertoire_png_preview_url;
      if (fresh && fresh !== currentSrc) {
        setCurrentSrc(fresh);
      }
    } catch {
      // Leave the broken image as-is; a transient failure shouldn't loop.
    }
  };

  if (!currentSrc) return null;

  return (
    <img src={currentSrc} alt={alt} style={style} className={className} onError={handleError} />
  );
};

export default PresignedImg;
